import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Settings } from "lucide-react";
import * as bookingApi from "@/services/bookingApi";
import type { Booking } from "@/types";
import type { BookingAnalytics } from "@/types";
import { formatDateInTimezone, formatInTimezone } from "@/utils/timezone";
import { HolidayNotificationBanner } from "@/components/dashboard/HolidayNotificationBanner";
import { InProgressReservations } from "@/components/booking/InProgressReservations";
import { ManualBookingModal } from "@/components/booking/ManualBookingModal";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";

type FilterStatus =
  | "all"
  | "pending_verification"
  | "pending_confirmation"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "rejected"
  | "no_show";

interface BookingManagerProps {
  providerTimezone?: string; // Provider's IANA timezone (e.g., "Europe/Sofia")
}

const BookingManager: React.FC<BookingManagerProps> = ({
  providerTimezone,
}) => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedAttendees, setSelectedAttendees] = useState<Booking[] | null>(
    null,
  );
  const [actionLoading, setActionLoading] = useState(false);
  const [showManualBookingModal, setShowManualBookingModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [internalNotesDraft, setInternalNotesDraft] = useState("");
  const [completionStats, setCompletionStats] = useState<
    BookingAnalytics["completion_time_stats"] | null
  >(null);

  useEffect(() => {
    if (selectedBooking) {
      setInternalNotesDraft(selectedBooking.internal_notes || "");
    }
  }, [selectedBooking]);

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const handleReject = async (id: string) => {
    setActionLoading(true);
    try {
      await bookingApi.rejectBooking(id);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to reject booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNoShow = async (id: string) => {
    setActionLoading(true);
    try {
      await bookingApi.markNoShow(id);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to mark no-show");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveInternalNotes = async (id: string) => {
    setActionLoading(true);
    try {
      const updated = await bookingApi.updateInternalNotes(id, internalNotesDraft);
      setSelectedBooking(updated);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to save internal notes");
    } finally {
      setActionLoading(false);
    }
  };

  // Timer effect for elapsed time when viewing a started but not completed booking
  useEffect(() => {
    if (
      !selectedBooking ||
      !(selectedBooking as any).started_at ||
      selectedBooking.completed_at
    ) {
      setElapsedSeconds(0);
      return;
    }

    const startedAt = new Date((selectedBooking as any).started_at).getTime();
    const updateElapsed = () => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [selectedBooking]);

  // Fetch completion stats when viewing a booking
  useEffect(() => {
    if (
      selectedBooking &&
      (selectedBooking.completed_at || (selectedBooking as any).started_at)
    ) {
      bookingApi
        .getCompletionStats()
        .then(setCompletionStats)
        .catch(() => setCompletionStats(null));
    } else {
      setCompletionStats(null);
    }
  }, [selectedBooking]);

  const fetchBookings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = { aggregate: true };
      if (filter !== "all") {
        params.status = filter;
      }

      const data = await bookingApi.getProviderBookings(params);
      setBookings((data || []) as any);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load bookings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (id: string) => {
    setActionLoading(true);
    try {
      await bookingApi.confirmBooking(id);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to confirm booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (id: string, reason?: string) => {
    setActionLoading(true);
    try {
      await bookingApi.cancelProviderBooking(id, reason);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to cancel booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (id: string) => {
    setActionLoading(true);
    try {
      await bookingApi.completeBooking(id);
      fetchBookings();
      setSelectedBooking(null);
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to complete booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async (id: string) => {
    setActionLoading(true);
    try {
      const updatedBooking = await bookingApi.startBookingTimer(id);
      setSelectedBooking(updatedBooking);
      fetchBookings();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to start booking");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (booking: Booking) => {
    // Prefer booking_datetime if available (UTC ISO8601)
    if (booking.booking_datetime && providerTimezone) {
      return formatDateInTimezone(booking.booking_datetime, providerTimezone, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
    // Fallback to booking_date
    return new Date(booking.booking_date + "T00:00:00").toLocaleDateString(
      "en-US",
      {
        weekday: "short",
        month: "short",
        day: "numeric",
      },
    );
  };

  const formatTime = (booking: Booking) => {
    // Prefer booking_datetime if available (UTC ISO8601)
    if (booking.booking_datetime && providerTimezone) {
      return formatInTimezone(booking.booking_datetime, providerTimezone, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    // Fallback to booking_time
    const [hours, minutes] = booking.booking_time.split(":");
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<
      string,
      { bg: string; text: string; labelKey: string }
    > = {
      pending_verification: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        labelKey: t("bookings.status.pending"),
      },
      pending_confirmation: {
        bg: "bg-orange-100",
        text: "text-orange-800",
        labelKey: "Pending confirmation",
      },
      confirmed: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        labelKey: t("bookings.status.confirmed"),
      },
      completed: {
        bg: "bg-green-100",
        text: "text-green-800",
        labelKey: t("bookings.status.completed"),
      },
      cancelled: {
        bg: "bg-gray-100",
        text: "text-gray-800",
        labelKey: t("bookings.status.cancelled"),
      },
      rejected: {
        bg: "bg-red-100",
        text: "text-red-800",
        labelKey: "Rejected",
      },
      no_show: {
        bg: "bg-red-100",
        text: "text-red-800",
        labelKey: "No-show",
      },
    };

    const badge = badges[status] || badges.pending_verification;

    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}
      >
        {badge.labelKey}
      </span>
    );
  };

  const showAttendees = (booking: Booking) => {
    // attendees are stored in the aggregated booking response
    setSelectedAttendees((booking as any).attendees || []);
  };

  const statusFilters: { value: FilterStatus; labelKey: string }[] = [
    { value: "all", labelKey: t("bookings.filters.all") },
    { value: "pending_verification", labelKey: t("bookings.filters.pending") },
    { value: "pending_confirmation", labelKey: "Pending confirmation" },
    { value: "confirmed", labelKey: t("bookings.filters.confirmed") },
    { value: "completed", labelKey: t("bookings.filters.completed") },
    { value: "cancelled", labelKey: t("bookings.filters.cancelled") },
    { value: "rejected", labelKey: "Rejected" },
    { value: "no_show", labelKey: "No-show" },
  ];

  return (
    <div className="p-6 lg:p-8">
      <HolidayNotificationBanner />
      <InProgressReservations />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("bookings.title")}
          </h1>
          <p className="mt-1 text-gray-500">{t("bookings.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard/booking-settings")}
          >
            <Settings className="w-4 h-4 mr-2" />
            {t("bookings.settingsButton")}
          </Button>
          <Button onClick={() => setShowManualBookingModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t("bookings.createBooking")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filter === f.value
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {f.labelKey}
          </button>
        ))}
      </div>

      {/* Bookings List */}
      <Card>
        <CardContent className="p-0">
        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-gray-600">{t("bookings.loading")}</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">{error}</p>
            <Button
              variant="link"
              onClick={fetchBookings}
              className="mt-4"
            >
              {t("bookings.tryAgain")}
            </Button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">{t("bookings.noBookings")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("bookings.table.customer")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("bookings.table.dateTime")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("bookings.table.services")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("bookings.table.attendance")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("bookings.table.status")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("bookings.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bookings.map((booking) => (
                <tr key={booking.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">
                      {booking.customer_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {booking.customer_email}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-gray-900">{formatDate(booking)}</div>
                    <div className="text-sm text-gray-500">
                      {formatTime(booking)}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-gray-900">
                      {booking.services?.map((s) => s.name).join(", ") ||
                        t("common.na")}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t("bookings.details.duration", {
                        minutes: booking.duration_minutes,
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    {(booking as any).is_multi_booking ? (
                      <div>
                        <span className="font-medium">
                          {(booking as any).attendance_count}
                        </span>
                        {(booking as any).attendance_count > 1 && (
                          <button
                            className="ml-2 text-primary hover:underline text-sm"
                            onClick={() => showAttendees(booking)}
                          >
                            {t("bookings.viewAttendees")}
                          </button>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(booking.status)}
                  </td>
                  <td className="px-4 py-4">
                    <Button
                      variant="link"
                      onClick={() => setSelectedBooking(booking)}
                      className="h-auto p-0"
                    >
                      {t("bookings.view")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-bold text-gray-900">
                  {t("bookings.details.title")}
                </h2>
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  {t("bookings.details.customer")}
                </h3>
                <p className="text-gray-900">{selectedBooking.customer_name}</p>
                <p className="text-sm text-gray-600">
                  {selectedBooking.customer_email}
                </p>
                {selectedBooking.assigned_user && (
                  <p className="text-sm text-gray-600">
                    {t("bookings.details.staff", {
                      name:
                        selectedBooking.assigned_user.full_name ||
                        selectedBooking.assigned_user.username,
                    })}
                  </p>
                )}
                {selectedBooking.customer_phone && (
                  <p className="text-sm text-gray-600">
                    {selectedBooking.customer_phone}
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  {t("bookings.details.appointment")}
                </h3>
                <p className="text-gray-900">
                  {formatDate(selectedBooking)} at {formatTime(selectedBooking)}
                </p>
                <p className="text-sm text-gray-600">
                  {t("bookings.details.duration", {
                    minutes: selectedBooking.duration_minutes,
                  })}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  {t("bookings.details.services")}
                </h3>
                <ul className="text-gray-900">
                  {selectedBooking.services?.map((s, idx) => (
                    <li key={idx}>
                      {s.name} ({s.duration_at_booking} min)
                      {s.price_at_booking !== null &&
                        s.price_at_booking > 0 &&
                        ` - €${s.price_at_booking.toFixed(2)}`}
                    </li>
                  ))}
                </ul>
                {selectedBooking.total_price !== null &&
                  selectedBooking.total_price > 0 && (
                    <p className="font-medium mt-2">
                      {t("bookings.details.total", {
                        amount: Number(
                          selectedBooking.total_price || 0,
                        ).toFixed(2),
                      })}
                    </p>
                  )}
              </div>

              {selectedBooking.customer_notes && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    {t("bookings.details.customerNotes")}
                  </h3>
                  <p className="text-gray-700">
                    {selectedBooking.customer_notes}
                  </p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Internal notes
                </h3>
                <textarea
                  value={internalNotesDraft}
                  onChange={(e) => setInternalNotesDraft(e.target.value)}
                  rows={3}
                  className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
                  placeholder="Private notes..."
                />
                <button
                  onClick={() => handleSaveInternalNotes(selectedBooking.id)}
                  disabled={actionLoading}
                  className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                >
                  Save internal notes
                </button>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  {t("bookings.details.status")}
                </h3>
                {getStatusBadge(selectedBooking.status)}
                {selectedBooking.cancelled_at &&
                  selectedBooking.cancellation_reason && (
                    <p className="text-sm text-red-600 mt-1">
                      {t("bookings.details.cancelled", {
                        reason: selectedBooking.cancellation_reason,
                      })}
                    </p>
                  )}
              </div>

              {/* Analytics/Completion Info */}
              {(selectedBooking as any).started_at && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    {t("bookings.details.startedAt")}
                  </h3>
                  <p className="text-gray-900">
                    {formatInTimezone(
                      (selectedBooking as any).started_at,
                      providerTimezone || "UTC",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      },
                    )}
                  </p>
                  {(selectedBooking as any).completed_at ? (
                    <p className="text-sm text-green-600 mt-1">
                      {t("bookings.details.completedIn", {
                        minutes: Math.round(
                          (new Date(
                            (selectedBooking as any).completed_at,
                          ).getTime() -
                            new Date(
                              (selectedBooking as any).started_at,
                            ).getTime()) /
                            60000,
                        ),
                      })}
                      {completionStats && (
                        <span className="text-gray-500 ml-2">
                          {t("bookings.details.avgCompletion", {
                            minutes: completionStats.avg.toFixed(1),
                          })}
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-primary mt-1 font-medium">
                      {t("bookings.details.elapsed", {
                        minutes: Math.floor(elapsedSeconds / 60),
                        seconds: elapsedSeconds % 60,
                      })}
                    </p>
                  )}

                  {/* Completion Progress Bar */}
                  {!(selectedBooking as any).completed_at && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{t("bookings.details.progress")}</span>
                        <span>
                          {Math.min(
                            100,
                            Math.round(
                              (elapsedSeconds /
                                (selectedBooking.duration_minutes * 60)) *
                                100,
                            ),
                          )}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-1000"
                          style={{
                            width: `${Math.min(100, Math.round((elapsedSeconds / (selectedBooking.duration_minutes * 60)) * 100))}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              {(selectedBooking.status === "pending_verification" ||
                selectedBooking.status === "pending_confirmation") && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      handleCancel(selectedBooking.id, "Cancelled by provider")
                    }
                    disabled={actionLoading}
                    className="text-red-600 hover:text-red-800"
                  >
                    {t("bookings.cancel")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handleReject(selectedBooking.id)}
                    disabled={actionLoading}
                    className="text-red-700 hover:text-red-900"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleConfirm(selectedBooking.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading
                      ? t("bookings.processing")
                      : t("bookings.confirm")}
                  </Button>
                </>
              )}
              {selectedBooking.status === "confirmed" &&
                !(selectedBooking as any).started_at && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        handleCancel(
                          selectedBooking.id,
                          "Cancelled by provider",
                        )
                      }
                      disabled={actionLoading}
                      className="text-red-600 hover:text-red-800"
                    >
                      {t("bookings.cancel")}
                    </Button>
                    <Button
                      onClick={() => handleStart(selectedBooking.id)}
                      disabled={actionLoading}
                    >
                      {actionLoading
                        ? t("bookings.processing")
                        : t("bookings.start")}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleNoShow(selectedBooking.id)}
                      disabled={actionLoading}
                      className="text-red-700 hover:text-red-900"
                    >
                      No-show
                    </Button>
                  </>
                )}
              {selectedBooking.status === "confirmed" &&
                (selectedBooking as any).started_at &&
                !selectedBooking.completed_at && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        handleCancel(
                          selectedBooking.id,
                          "Cancelled by provider",
                        )
                      }
                      disabled={actionLoading}
                      className="text-red-600 hover:text-red-800"
                    >
                      {t("bookings.cancel")}
                    </Button>
                    <Button
                      onClick={() => handleComplete(selectedBooking.id)}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {actionLoading
                        ? t("bookings.processing")
                        : t("bookings.markComplete")}
                    </Button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Attendees Modal for Multi-Booking */}
      {selectedAttendees && selectedAttendees.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-bold text-gray-900">{t("bookings.attendees")}</h2>
                <button
                  onClick={() => setSelectedAttendees(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {selectedAttendees.map((attendee, idx) => (
                <div
                  key={idx}
                  className="border-b last:border-b-0 pb-3 last:pb-0"
                >
                  <div className="font-medium text-gray-900">
                    {attendee.customer_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {attendee.customer_email}
                  </div>
                  {attendee.customer_phone && (
                    <div className="text-sm text-gray-500">
                      {attendee.customer_phone}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setSelectedAttendees(null)}
              >
                {t("bookings.close")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ManualBookingModal
        isOpen={showManualBookingModal}
        onClose={() => setShowManualBookingModal(false)}
        onSuccess={() => {
          setShowManualBookingModal(false);
          fetchBookings();
        }}
        profileId={profile?.id || ""}
      />
    </div>
  );
};

export default BookingManager;
